import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/app/providers/AuthProvider';
import type { Client } from '@/types/database';

interface CurrentClientContextValue {
  clientId: string | null;
  client: Client | null;
  loading: boolean;
  setClientId: (id: string | null) => void;
}

const CurrentClientContext = createContext<CurrentClientContextValue | null>(null);

const STORAGE_KEY_PREFIX = 'clinance:currentClientId:';

function readPersistedClientId(userId: string | undefined): string | null {
  if (!userId || typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(STORAGE_KEY_PREFIX + userId);
  } catch {
    return null;
  }
}

function writePersistedClientId(userId: string | undefined, id: string | null): void {
  if (!userId || typeof window === 'undefined') return;
  try {
    if (id) window.localStorage.setItem(STORAGE_KEY_PREFIX + userId, id);
    else window.localStorage.removeItem(STORAGE_KEY_PREFIX + userId);
  } catch {
    // ignore (modo privado, quota etc.)
  }
}

export function useCurrentClient(): CurrentClientContextValue {
  const ctx = useContext(CurrentClientContext);
  if (!ctx) throw new Error('useCurrentClient must be used inside CurrentClientProvider');
  return ctx;
}

export function CurrentClientProvider({ children }: { children: ReactNode }) {
  const { profile, user } = useAuth();

  // Para role 'cliente', o client_id é fixo do profile.
  // Para staff, hidrata do localStorage (chave por usuário) e persiste a cada mudança.
  const [selectedClientId, setSelectedClientId] = useState<string | null>(() =>
    readPersistedClientId(user?.id),
  );

  // Quando o usuário muda (login em outra conta), re-hidrata do storage da conta nova.
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!user?.id) {
      setSelectedClientId(null);
      return;
    }
    setSelectedClientId(readPersistedClientId(user.id));
  }, [user?.id]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const clientId = profile?.role === 'cliente' ? (profile.client_id ?? null) : selectedClientId;

  const { data: client = null, isLoading: loading } = useQuery({
    queryKey: ['client', clientId],
    enabled: Boolean(clientId),
    staleTime: 1000 * 60 * 5,
    queryFn: async () => {
      if (!clientId) return null;
      const { data } = await supabase.from('clients').select('*').eq('id', clientId).single();
      return data;
    },
  });

  const setClientId = useCallback(
    (id: string | null) => {
      setSelectedClientId(id);
      writePersistedClientId(user?.id, id);
    },
    [user?.id],
  );

  return (
    <CurrentClientContext.Provider
      value={{ clientId, client, loading: loading && Boolean(clientId), setClientId }}
    >
      {children}
    </CurrentClientContext.Provider>
  );
}
